import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { truckingReportAPI, truckingReportExtAPI } from '../../api/waybillAPI';
import {
  PHP, fmtDate, PrintShell, PrintLetterhead, PrintSignatures, SummaryRows, groupBySpan,
} from './PSACCBillPrintLayout';

// The two PSACC statements for ONE bill, as a single print job: the billing
// statement, then the additional-stripping statement on its own sheet. They are
// issued together and handed to the carrier together, so they print together.
//
// Numbering — the two documents carry CONSECUTIVE SOA Numbers:
//   billing  = bill.SOANumber          (e.g. 2026-061)
//   stripping = bill.SOANumber + 1     (e.g. 2026-062)
// The stripping number is derived from the bill, never from the global
// truckingSOACounters sequence, and is reserved by a get-or-create keyed on the
// bill. Mounting this page any number of times — a reprint, the modal's
// "Save & Print" path, React StrictMode's double-invoke in dev — yields the
// same number and creates exactly one reservation.
//
// A failed reservation is a hard stop: the failure that matters is "that number
// already belongs to something else", and printing through it would put a
// duplicate SOA Number on a carrier-facing document.
export default function PSACCCombinedBillPrintPage() {
  const { billId } = useParams();
  const [billing, setBilling] = useState(null);
  const [stripping, setStripping] = useState(null);
  const [stripSOANumber, setStripSOANumber] = useState('');
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [std, strip] = await Promise.all([
          truckingReportAPI.psaccStandard(billId),
          truckingReportAPI.psaccStripping(billId),
        ]);
        if (cancelled) return;

        // A bill with no additional stripping has no stripping statement to
        // issue, so the second sheet is skipped entirely.
        const hasStripping = (strip.data?.summary?.totalStripping || 0) > 0;

        if (hasStripping) {
          // Reserved ONLY once we know the statement will actually be printed.
          // This call permanently consumes a number out of the SOA sequence, so
          // it must not run for a bill that produces no stripping statement —
          // that would leave an unexplainable gap in a compliance-facing
          // sequence. Hence the sequential await rather than folding it into
          // the Promise.all above.
          const statement = await truckingReportExtAPI.getAdditionalStrippingSOANumber(billId);
          if (cancelled) return;
          const reserved = statement.data?.soaNumber;
          if (!reserved) {
            // Can't happen against the current endpoint, which always returns a
            // number on success — but without this the page would render fine
            // and simply never print, a worse failure than saying so.
            setError('The additional-stripping statement number could not be issued. Nothing was printed.');
            return;
          }
          setStripping(strip.data);
          setStripSOANumber(reserved);
        } else {
          // No stripping statement will be issued, so hand its held number back
          // — otherwise it sits claimed by a document that never exists and
          // leaves a hole in the SOA sequence. Best-effort: a failure here must
          // not stop the billing statement from printing, and the release is
          // idempotent so a later reprint retries it.
          try {
            await truckingReportExtAPI.releaseAdditionalStrippingSOANumber(billId);
          } catch {
            // Intentionally swallowed — see above.
          }
          if (cancelled) return;
        }

        setBilling(std.data);
        setReady(true);
      } catch (e) {
        if (cancelled) return;
        setError(
          e?.response?.data?.message
          || e?.response?.data?.error
          || 'Failed to load the PSACC statements. Nothing was printed.'
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [billId]);

  useEffect(() => {
    // One print for the whole job, once everything the job needs is in hand —
    // never on the error path. `ready` is only set after the stripping
    // statement has been numbered, when there is one to number.
    if (!ready || !billing) return;
    document.title = stripSOANumber
      ? `PSACC Statements — ${billing.soaNumber} / ${stripSOANumber}`
      : `PSACC Billing — ${billing.soaNumber}`;
    const t = setTimeout(() => window.print(), 400);
    return () => clearTimeout(t);
  }, [ready, billing, stripSOANumber]);

  if (loading) return <p style={{ padding: 40 }}>Loading statements…</p>;
  if (error) return <p style={{ padding: 40, color: '#c00' }}>{error}</p>;

  const billingSpans = groupBySpan(billing.lines || [], (r) => r.rates || 0);
  // `stripping` is only populated when the bill actually has additional
  // stripping; otherwise the second sheet is not rendered at all.
  // Span subtotal follows what the statement itemises: ADD. STRIPPING only —
  // the same basis as the statement's TOTAL AMOUNT DUE.
  const strippingSpans = stripping
    ? groupBySpan(
        (stripping.lines || []).filter((r) => (r.additionalStripping || 0) > 0),
        (r) => r.additionalStripping || 0
      )
    : [];

  return (
    <PrintShell>
      {/* ── Document 1 — PSACC Billing Statement ───────────────────────────── */}
      <table>
        <thead>
          <PrintLetterhead
            colSpan={10}
            trucker={billing.trucker}
            carrier={billing.carrier}
            statementNo={billing.soaNumber}
          />
          <tr>
            <th>DATE</th>
            <th>PLATE NO.</th>
            <th>WB NO.</th>
            <th>VESSEL / VOY. NO.</th>
            <th>VAN NO.</th>
            <th>CONSIGNEE</th>
            <th>LOCATION</th>
            <th style={{ textAlign: 'right' }}>RATES</th>
            <th style={{ textAlign: 'right' }}>TOTAL AMOUNT PER SPAN</th>
            <th>DATE RETURN</th>
          </tr>
        </thead>
        <tbody>
          {billingSpans.length === 0 ? (
            <tr><td colSpan={10} style={{ textAlign: 'center', padding: 16 }}>No waybills attached to this bill.</td></tr>
          ) : billingSpans.map((span, si) => (
            <React.Fragment key={span.key}>
              {span.rows.map((r, ri) => (
                <tr key={ri}>
                  <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(r.date)}</td>
                  <td>{r.plateNo}</td>
                  <td>{r.waybillNo}</td>
                  <td>{r.vesselVoyage}</td>
                  <td>{r.vanNo}</td>
                  <td>{r.consignee}</td>
                  <td>{r.location}</td>
                  <td style={{ textAlign: 'right' }}>₱{PHP(r.rates)}</td>
                  {ri === 0 && (
                    <td style={{ textAlign: 'right' }} className="merged-total" rowSpan={span.rows.length}>
                      Php {PHP(span.subtotal)}
                    </td>
                  )}
                  <td>{r.dateReturn ? fmtDate(r.dateReturn) : '—'}</td>
                </tr>
              ))}
              {si < billingSpans.length - 1 && (
                <tr className="spacer-row"><td colSpan={10}>&nbsp;</td></tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>

      {billing.summary && billingSpans.length > 0 && (
        <SummaryRows
          rows={[
            { label: 'TOTAL:', value: billing.summary.total },
            { label: 'VAT:', value: billing.summary.vat },
            { label: 'Net of VAT:', value: billing.summary.netOfVat },
            { label: 'CWT:', value: billing.summary.cwt },
            { label: 'TOTAL AMOUNT DUE:', value: billing.summary.totalAmountDue, rule: true },
          ]}
        />
      )}

      <PrintSignatures cfo={billing.cfo} />

      {/* ── Document 2 — Additional Stripping Statement (new sheet) ───────────
          Omitted entirely for a bill with no additional stripping: no page
          break, no blank sheet, and no SOA Number consumed for it. */}
      {stripping && (
      <div className="doc-break">
        <table>
          <thead>
            <PrintLetterhead
              colSpan={10}
              trucker={stripping.trucker}
              carrier={stripping.carrier}
              statementNo={stripSOANumber}
            />
            <tr>
              <th>DATE</th>
              <th>PLATE NO.</th>
              <th>WB NO.</th>
              <th>VESSEL / VOY. NO.</th>
              <th>VAN NO.</th>
              <th>CONSIGNEE</th>
              <th>LOCATION</th>
              <th style={{ textAlign: 'right' }}>ADD. STRIPPING</th>
              <th style={{ textAlign: 'right' }}>RATES</th>
              <th style={{ textAlign: 'right' }}>TOTAL AMOUNT PER SPAN</th>
            </tr>
          </thead>
          <tbody>
            {strippingSpans.length === 0 ? (
              <tr><td colSpan={10} style={{ textAlign: 'center', padding: 16 }}>No stripping charges on this bill.</td></tr>
            ) : strippingSpans.map((span, si) => (
              <React.Fragment key={span.key}>
                {span.rows.map((r, ri) => (
                  <tr key={ri}>
                    <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(r.date)}</td>
                    <td>{r.plateNo}</td>
                    <td>{r.waybillNo}</td>
                    <td>{r.vesselVoyage}</td>
                    <td>{r.vanNo}</td>
                    <td>{r.consignee}</td>
                    <td>{r.location}</td>
                    <td style={{ textAlign: 'right' }}>₱{PHP(r.additionalStripping)}</td>
                    {/* RATES is intentionally blank per line — this statement
                        itemises ADD. STRIPPING only. The RATE figure appears
                        once, in the summary below. */}
                    <td style={{ textAlign: 'right' }}></td>
                    {ri === 0 && (
                      <td style={{ textAlign: 'right' }} className="merged-total" rowSpan={span.rows.length}>
                        Php {PHP(span.subtotal)}
                      </td>
                    )}
                  </tr>
                ))}
                {si < strippingSpans.length - 1 && (
                  <tr className="spacer-row"><td colSpan={10}>&nbsp;</td></tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>

        {stripping.summary && strippingSpans.length > 0 && (
          <SummaryRows
            rows={[
              // "RATE" is the base StrippingFee, not the trucking rate — a
              // legacy label kept to match the printed form. RATE and
              // STRIPPING are reference figures; TOTAL AMOUNT DUE is the
              // additional stripping alone and is NOT their sum.
              { label: 'RATE:', value: stripping.summary.totalRate },
              { label: 'STRIPPING:', value: stripping.summary.totalStripping },
              { label: 'TOTAL AMOUNT DUE:', value: stripping.summary.totalAmountDue, rule: true },
            ]}
          />
        )}

        <PrintSignatures cfo={stripping.cfo} />
      </div>
      )}
    </PrintShell>
  );
}
